import type { ReactElement, CSSProperties } from 'react'
import type { GlyphProps } from './shared'

interface ImgGlyphProps extends GlyphProps {
  src: string
  alt?: string
  /** Extra CSS filter appended to the default drop-shadow. Used by
   *  units that share a bitmap with another unit so a per-unit tint
   *  differentiates the two silhouettes. Typical value:
   *  'hue-rotate(180deg) saturate(0.8)'. */
  tint?: string
}

/**
 * ImgGlyph — wraps an image asset for glyphs that are bitmap art rather
 * than inline SVG. Expects PNGs with a transparent background (generated
 * via packages/client/scripts/strip-white-bg.mjs); applies the same
 * drop-shadow as SVG glyphs so they sit consistently on the page.
 *
 * Place asset files under client/public/glyph-assets/ and reference by
 * path "/glyph-assets/<name>.png".
 */
export const ImgGlyph = ({ src, size = 110, alt, tint }: ImgGlyphProps): ReactElement => {
  // The `size` prop ships only as HTML width/height *attributes* on the
  // <img>; no inline width/height styles on either the wrapper or img.
  // That keeps the prop value as the default rendered size (so existing
  // consumers like the lineup at size=70 are unchanged) while letting
  // any descendant CSS rule (e.g. `.shop-card__art img { width: clamp(...) }`)
  // override the size at narrow viewports. Inline styles win over CSS
  // rules, so locking the size inline made bitmap glyphs un-resizable.
  const baseFilter = 'drop-shadow(0 3px 0 rgba(42,34,26,0.18))'
  const imgStyle: CSSProperties = {
    objectFit: 'contain',
    filter: tint ? `${baseFilter} ${tint}` : baseFilter,
    display: 'block',
  }
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <img src={src} alt={alt ?? ''} width={size} height={size} style={imgStyle} />
    </div>
  )
}
